<template>
  <div class="card card-custom card-stretch mb-5">
    <div class="card-header align-items-center border-0 mt-5">
      <h3 class="card-title align-items-start flex-column">
        <span class="card-label fw-bolder text-dark fs-3">
          Моя выгода при обратном выкупе
        </span>
      </h3>
    </div>
    <div class="card-body pt-9">
      <div class="table-responsive">
        <table class="table table-borderless table-hover">
          <thead>
            <tr class="fw-bold text-gray-800 border-bottom border-gray-200">
              <th></th>
              <Year v-for="col in columns" :year="col" :status="share[col].status"/>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th class="fw-bold text-gray-800">
                Моя доля
              </th>
              <th v-for="col in columns" class="text-nowrap">
                <Percent v-if="share[col].hasShare" :value="share[col].percent"/>
                <span v-else>—</span>
                <HelpMessage v-if="share[col].hasShare">{{ share[col].tooltipPercent }}</HelpMessage>
              </th>
            </tr>
            <tr>
              <th class="fw-bold text-gray-800">
                Коэффициент вызревания
              </th>
              <th v-for="col in columns" class="text-nowrap">
                <span>{{ share[col].aging ? share[col].aging.toFixed(2) : '—' }}</span>
                <HelpMessage v-if="share[col].hasShare">
                  0.25 — В момент выдачи<br/>
                  0.50 — В марте {{ col + 2 }} года<br/>
                  0.75 — В марте {{ col + 3 }} года<br/>
                  1.00 — В марте {{ col + 4 }} года<br/>
                </HelpMessage>
              </th>
            </tr>
            <tr class="border-bottom border-gray-200">
              <th class="fw-bold text-gray-800">
                Стоимость на момент выдачи
              </th>
              <th v-for="col in columns" class="text-nowrap">
                <Money :value="startPrice[col]"/>
                <HelpMessage v-if="share[col].hasShare">{{ share[col].tooltipStartPrice }}</HelpMessage>
              </th>
            </tr>
            <tr v-for="row in rows">
              <th class="fw-bold text-gray-800">Стоимость в {{ (row)*1+1 }} году</th>
              <th v-for="col in columns">
                <span
                  v-if="shouldStrikePrice(row, col) && price[row][col] !== null && price[row][col] !== undefined"
                  class="text-muted text-decoration-line-through"
                >
                  {{ formatMoney(price[row][col]) }}
                </span>
                <Money v-else :value="price[row][col]"/>
              </th>
            </tr>
            <tr class="border-top border-gray-200">
              <th class="fw-bold text-gray-800">
                Моя выгода при внутреннем выкупе на сегодня
              </th>
              <th v-for="col in columns" class="text-nowrap">
                <span
                  v-if="share[col].rawStatus === statusBought"
                  class="text-muted text-decoration-line-through"
                >
                  {{ formatMoney(hypotheticalSellPrice[col]) }}
                </span>
                <Money v-else :value="sellPrice[col]"/>
                <HelpMessage v-if="share[col].hasShare">{{ share[col].tooltipSellPrice }}</HelpMessage>
              </th>
            </tr>
            <tr>
              <th class="fw-bold text-gray-800">
                Итого на сегодня
              </th>
              <th class="text-nowrap">
                <Money :value="totalSellPrice"/>
              </th>
            </tr>
            <tr>
              <th class="fw-bold text-gray-800">
                <span class="d-inline-flex align-items-center text-nowrap">
                  Прогноз в следующем году
                  <HelpMessage>
                    {{ nextYearForecastTooltip }}
                  </HelpMessage>
                </span>
              </th>
              <th class="text-nowrap">
                <div class="d-inline-flex align-items-center">
                  <Money :value="totalForecastSellPrice"/>
                  <small
                    v-if="forecastGrowthPercent !== null"
                    class="d-inline-block px-1 py-0 rounded fw-semibold ms-2"
                    style="font-size: 0.72rem;"
                    :class="forecastGrowthPercent >= 0 ? 'bg-light-primary text-primary' : 'bg-light-danger text-danger'"
                  >
                    {{ formatGrowthPercent(forecastGrowthPercent) }}
                  </small>
                </div>
              </th>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Percent from "./components/Percent";
import Money from "./components/Money";
import Year from "./components/Year";
import {money} from "../helpers/formatter";
import HelpMessage from "./components/HelpMessage";

const currentYear = (new (Date)).getFullYear()
const agingYear = (new (Date)).getMonth() >= 2 ? currentYear : (currentYear - 1);

const statusAliases = {
  planned: 'in_progress'
}

const statusPlanned = 'planned'
const statusBought = 'bought'

const grade = {
  common: 'Базовый опцион',
  heightened: 'Опцион за достижения',
}

export default {
  components: {HelpMessage, Year, Money, Percent},
  props: {
    my: Array,
    years: Array,
    statistic: Array,
  },

  data() {
    const columns = [], rows = {}
    const startPrice = {}, sellPrice = {}, hypotheticalSellPrice = {}, price = {}, share = {}, revenue = {}
    let totalSellPrice = 0
    let totalForecastSellPrice = 0
    const forecastAgingYear = agingYear + 1
    const nextDistributionYear = (new Date()).getMonth() < 2 ? currentYear : (currentYear + 1)
    const calcAging = (year, baseYear) => {
      const diff = baseYear - year
      if (diff <= 0) {
        return 0
      }

      return diff > 4 ? 1 : (diff / 4)
    }

    let maxYear = 0

    this.statistic.forEach(v => {
      maxYear = Math.max(v.year, maxYear)
      revenue[v.year] = v.projected_revenue > 0 && v.year >= currentYear ? v.projected_revenue : v.revenue;
    })

    this.my.forEach(v => {
      columns.push(v.year)

      let aging = calcAging(v.year, agingYear)
      if (v.status === statusPlanned) {
        aging = 0
      }

      share[v.year] = {
        hasShare: true,
        percent: v.percent,
        status: statusAliases[v.status] ?? v.status,
        rawStatus: v.status,
        boughtAt: v.bought_at ?? null,
        aging: aging,
        tooltipPercent: grade[v.grade] + ' ÷ Кол-во участников',
        tooltipStartPrice: 'Выручка за ' + (v.year - 1) + ' год × Моя доля',
        tooltipSellPrice: '(Выручка за прошлый год × Моя доля − Стоимость на момент выдачи) × Коэф. вызревания',
      }

      startPrice[v.year] = revenue[v.year-1] * v.percent / 100
      hypotheticalSellPrice[v.year] = (revenue[agingYear - 1] * v.percent / 100 - startPrice[v.year]) * aging
      sellPrice[v.year] = null
      if (v.status !== statusBought) {
        sellPrice[v.year] = hypotheticalSellPrice[v.year]
        totalSellPrice += sellPrice[v.year]

        const forecastAging = calcAging(v.year, forecastAgingYear)
        const forecastRevenue = revenue[forecastAgingYear - 1] || 0
        const forecastSellPrice = (forecastRevenue * v.percent / 100 - startPrice[v.year]) * forecastAging
        totalForecastSellPrice += forecastSellPrice > 0 ? forecastSellPrice : 0
      }

      for (let key = v.year; key <= maxYear; key++) {
        if (typeof price[key] === 'undefined') {
          price[key] = {}
        }

        rows[key] = true
        price[key][v.year] = revenue[key] * v.percent / 100
      }
    })

    if (columns.length === 0) {
      this.years.forEach(v => {
        columns.push(v.year)
        share[v.year] = {
          hasShare: false,
          percent: null,
          status: v.status,
          rawStatus: null,
          boughtAt: null,
          aging: null,
          tooltipPercent: '',
          tooltipStartPrice: '',
          tooltipSellPrice: '',
        }
      })
    }

    return {
      columns,
      rows: Object.keys(rows),

      revenue,
      share,
      price,
      startPrice,
      hypotheticalSellPrice,
      sellPrice,
      totalSellPrice,
      totalForecastSellPrice,
      nextDistributionYear,
      statusBought,
    };
  },

  computed: {
    nextYearForecastTooltip() {
      return `Прогноз на март ${this.nextDistributionYear} года`
    },
    forecastGrowthPercent() {
      if (this.totalSellPrice <= 0) {
        return null
      }

      return ((this.totalForecastSellPrice - this.totalSellPrice) / this.totalSellPrice) * 100
    },
  },

  methods: {
    formatMoney(value) {
      return value > 0 ? money(value) : '—'
    },
    formatGrowthPercent(value) {
      const rounded = Math.round(value)
      const sign = rounded > 0 ? '+' : ''
      return `${sign}${rounded}%`
    },
    strikeFromYear(col) {
      const boughtAt = this.share[col]?.boughtAt
      if (!boughtAt) {
        return agingYear
      }

      const [yearRaw, monthRaw] = boughtAt.split('-')
      const year = Number(yearRaw)
      const month = Number(monthRaw)

      if (!year || !month) {
        return agingYear
      }

      // Distribution is in March:
      // - buyout before March -> strike from current year
      // - buyout in March or later -> strike from next year
      return month < 3 ? year : (year + 1)
    },
    shouldStrikePrice(row, col) {
      if (this.share[col]?.rawStatus !== statusBought) {
        return false
      }

      const displayYear = Number(row) + 1
      return displayYear >= this.strikeFromYear(col)
    },
  },
}
</script>
